<template>
  <div class="agent-message-entry">
    <!-- User message -->
    <div v-if="msg.type === 'user'" class="copilotKitMessage copilotKitUserMessage">
      {{ msg.content }}
    </div>

    <!-- Assistant message -->
    <div v-else-if="msg.type === 'assistant'" class="copilotKitMessage copilotKitAssistantMessage">
      <div v-if="msg.isStreaming && !msg.content" class="agent-typing-indicator">
        <span class="agent-typing-dot"></span>
        <span class="agent-typing-dot"></span>
        <span class="agent-typing-dot"></span>
      </div>
      <div v-else class="agent-response-card">
        <div class="agent-response-accent"></div>
        <div class="agent-response-body">
          <div class="copilotKitMarkdownElement" v-html="renderedMarkdown"></div>
        </div>
      </div>
      <div v-if="!msg.isStreaming" class="copilotKitMessageControls">
        <button class="copilotKitMessageControlButton" @click="copyMessage" :title="copied ? 'Copied!' : 'Copy'">
          <span v-html="copied ? CheckIcon : CopyIcon"></span>
        </button>
      </div>
    </div>

    <!-- Step row (inside trace group) -->
    <div v-else-if="msg.type === 'step'" class="agent-step" :class="msg.status === 'done' ? 'agent-step-done' : 'agent-step-active'">
      <span class="agent-step-dot-sm" :class="msg.status === 'done' ? 'dot-done' : 'dot-active'"></span>
      <span class="agent-step-text">{{ msg.content }}</span>
    </div>

    <!-- Tool call chip -->
    <div v-else-if="msg.type === 'tool'" class="agent-tool-chip" :class="{ running: msg.status === 'running' }">
      <button class="agent-tool-toggle" @click="msg.args && (toolExpanded = !toolExpanded)">
        <span class="agent-tool-dot" :class="msg.status === 'running' ? 'dot-running' : 'dot-done'"></span>
        <span class="agent-tool-label">{{ toolLabel }}</span>
        <span v-if="msg.args" class="agent-tool-chevron">{{ toolExpanded ? '▴' : '▾' }}</span>
      </button>
      <div v-if="toolExpanded && msg.args" class="agent-tool-args">
        <pre>{{ formattedArgs }}</pre>
      </div>
    </div>

    <!-- Suggestion pills -->
    <div v-else-if="msg.type === 'suggestions'" class="agent-suggestions">
      <hr class="agent-suggestions-divider" />
      <div class="agent-suggestions-label">Or try one of these:</div>
      <button
        v-for="s in msg.suggestions"
        :key="s.text"
        class="agent-suggestion-pill"
        @click="$emit('send', s.text)"
      >
        <span v-if="s.icon" class="agent-suggestion-icon">{{ s.icon }}</span>
        {{ s.text }}
      </button>
    </div>

    <!-- Data table -->
    <div v-else-if="msg.type === 'data'" class="agent-data-card">
      <div class="agent-data-title">{{ msg.title }}</div>
      <table class="agent-data-table">
        <tbody>
          <tr v-for="(value, key) in msg.data" :key="key">
            <td class="agent-data-key">{{ key }}</td>
            <td class="agent-data-value">{{ value }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import { CopyIcon, CheckIcon } from './agentIcons';

const renderer = new marked.Renderer();
const wrapClass = (tag, str) => str.replace(`<${tag}>`, `<${tag} class="copilotKitMarkdownElement">`);
renderer.paragraph = t => wrapClass('p', new marked.Renderer().paragraph(t));
renderer.heading = (t, l, raw, slugger) => wrapClass(`h${l}`, new marked.Renderer().heading(t, l, raw, slugger));
renderer.list = (b, o) => wrapClass(o ? 'ol' : 'ul', new marked.Renderer().list(b, o));
renderer.listitem = t => wrapClass('li', new marked.Renderer().listitem(t));
renderer.blockquote = q => wrapClass('blockquote', new marked.Renderer().blockquote(q));
renderer.link = (h, ti, t) => new marked.Renderer().link(h, ti, t).replace('<a ', '<a class="copilotKitMarkdownElement" ');
marked.setOptions({ breaks: true, gfm: true, renderer });

const TOOL_LABELS = {
  // Browser capability
  page_snapshot:     'Reading page',
  navigate_to:       'Navigating',
  click_element:     'Clicking element',
  type_text:         'Entering text',
  select_option:     'Selecting option',
  wait_for_page:     'Waiting',
  extract_data:      'Extracting data',
  // Recall capability
  check_call_state:  'Checking call state',
  speak:             'Speaking to patient',
  leave_voicemail:   'Leaving voicemail',
  send_sms:          'Sending SMS',
  transfer_to_human: 'Transferring to front desk',
  book_appointment:  'Booking appointment',
  end_call:          'Ending call',
};

export default {
  name: 'AgentMessage',
  props: { msg: { type: Object, required: true } },
  data() {
    return { toolExpanded: false, copied: false, CopyIcon, CheckIcon };
  },
  computed: {
    renderedMarkdown() {
      const content = this.msg.content || '';
      const cursor = this.msg.isStreaming ? '\u258D' : '';
      return marked.parse(content + cursor);
    },
    formattedArgs() {
      try { return JSON.stringify(JSON.parse(this.msg.args), null, 2); }
      catch { return this.msg.args; }
    },
    toolLabel() {
      return TOOL_LABELS[this.msg.toolName] || this.msg.toolName;
    },
  },
  methods: {
    copyMessage() {
      navigator.clipboard.writeText(this.msg.content).then(() => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      });
    },
  },
};
</script>
